/**
 * SRA AI Search Service
 * Provides methods to interact with the SRA AI search API
 */
import api from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Perform an AI-powered search for SRA data
 * 
 * @param {Object} params - Search parameters
 * @param {string} params.q - Natural language search query (note: changed from 'query' to 'q' to match API)
 * @param {string} [params.city] - Optional city filter
 * @param {string} [params.practice] - Optional practice area filter
 * @param {number} [params.rating] - Optional minimum rating filter
 * @param {string} [params.category] - Optional regulatory category filter
 * @param {AbortSignal} [signal] - Optional abort signal
 * @returns {Promise<Object>} - Search results
 */
export const searchSRA = async (params, signal = null) => {
  try {
    console.log('SRA search params:', params);
    
    const token = localStorage.getItem('token');
    
    // Authentication is optional for the new optimized search API
    // if (!token) {
    //   throw new Error('AUTHENTICATION_REQUIRED');
    // }
    
    const queryParams = new URLSearchParams();
    
    if (params.q) queryParams.append('q', params.q);
    if (params.city) queryParams.append('city', params.city);
    if (params.practice) queryParams.append('practice', params.practice);
    if (params.rating) queryParams.append('rating', params.rating);
    if (params.category) queryParams.append('category', params.category);
    if (params.preservedSearch) queryParams.append('preservedSearch', params.preservedSearch);
    
          const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization header only if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_URL}/api/search/rag?${queryParams.toString()}`,
        {
          method: 'GET',
          headers,
          signal: signal // Pass abort signal to fetch
        }
      );

    // Handle authentication (optional for new API)
    if (response.status === 401) {
      throw new Error('AUTHENTICATION_REQUIRED');
    }
    
    // Handle credits (may not apply to new optimized API)
    if (response.status === 402) {
      const errorData = await response.json();
      const creditsError = new Error('CREDITS_EXHAUSTED');
      creditsError.creditsInfo = errorData;
      throw creditsError;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    console.log('SRA search response:', data);
    
    // Check if the response indicates AI search is not configured
    if (data.isConfigured === false) {
      throw new Error('AI search is not properly configured on the server');
    }
    
    return data;
    
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('SRA search was cancelled');
      throw new Error('Search cancelled');
    }
    
    console.error('SRA search error:', error);
    
    // Handle authentication and credit errors first
    if (error.message === 'AUTHENTICATION_REQUIRED') {
      throw error;
    }
    
    if (error.message === 'CREDITS_EXHAUSTED') {
      throw error;
    }
    
    // Handle different types of errors
    if (error.response) {
      const { status, data } = error.response;
      
      // Handle quota errors specifically
      if (status === 503 || (data && data.message && (
        data.message.includes('quota') || 
        data.message.includes('429') ||
        data.message.includes('billing') ||
        data.message.includes('InsufficientQuotaError')
      ))) {
        const quotaError = new Error('AI search quota exceeded. Please try again later or use standard search.');
        quotaError.isQuotaError = true;
        quotaError.originalError = data;
        throw quotaError;
      }
      
      // Handle configuration errors
      if (status === 503 && data.isConfigured === false) {
        const configError = new Error('AI search is temporarily unavailable');
        configError.isConfigError = true;
        configError.originalError = data;
        throw configError;
      }
      
      // Handle other HTTP errors
      if (status === 400) {
        throw new Error(data.error || 'Invalid search query');
      }
      
      if (status === 500) {
        // Check if it's specifically an OpenAI error
        if (data.message && (
          data.message.includes('OpenAI') || 
          data.message.includes('embedding') ||
          data.message.includes('vector search')
        )) {
          const aiError = new Error('AI search service is temporarily unavailable');
          aiError.isAiError = true;
          aiError.originalError = data;
          throw aiError;
        }
        
        throw new Error(data.error || 'Search service error');
      }
      
      // Generic HTTP error
      throw new Error(`Search failed with status ${status}`);
    }
    
    // Handle network errors
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      throw new Error('Network error - please check your connection');
    }
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      throw new Error('Search request timed out - please try again');
    }
    
    // Re-throw the original error if we can't categorize it
    throw error;
  }
};

/**
 * Check if AI search is properly configured
 * 
 * @returns {Promise<boolean>} - True if AI search is available
 */
export const checkSRASearchStatus = async () => {
  try {
    const response = await api.get('/sra/status');
    return response.data.isConfigured;
  } catch (error) {
    console.error('Error checking SRA search status:', error);
    return false;
  }
};

/**
 * Get user's current AI search credits
 * 
 * @returns {Promise<Object>} - User's credit information
 */
export const getUserAiCredits = async () => {
  try {
    const response = await api.get('/sra/credits');
    return response.data.aiCredits; // Extract the aiCredits object from the response
  } catch (error) {
    console.error('Error getting user AI credits:', error);
    throw error;
  }
};

/**
 * Get user's AI search history
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @returns {Promise<Object>} - Search history with pagination info
 */
export const getSearchHistory = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const response = await api.get(`/sra/history?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error getting search history:', error);
    throw error;
  }
};

export default {
  searchSRA,
  checkSRASearchStatus,
  getUserAiCredits,
  getSearchHistory
}; 